import { createAction } from '@reduxjs/toolkit'
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { RootState } from 'app'
import Api, { paths } from 'shared/services/Api'
import { selectors } from 'shared/store'
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  Filters,
  AddInvoiceRequest,
  AddInvoiceResponse,
  GetInvoiceResponse,
  GetFilePreviewResponse,
  FileImportErrorCode,
  InvoiceImport,
  Invoice,
  InvoiceHistoryItem,
} from 'shared/types'
import { downloadFile, invert } from 'shared/utils'
import {
  FileWithMappingOperationPayload,
  GetFileImportSummaryResponse,
  AddInvoiceDialogState,
  MultipleDeleteStatus,
  MultipleNotificationStatus,
  MultipleSetAsPaidStatus,
} from './types'

const NAMESPACE = 'modules/Invoice'

export const setFilters = createAction<Filters>(`${NAMESPACE}/setFilters`)

export const patchFilters = createAction<Partial<Filters>>(
  `${NAMESPACE}/patchFilters`
)

export const clearFilters = createAction(`${NAMESPACE}/clearFilters`)

export const closeAddDueSuccessDialog = createAction<boolean>(
  `${NAMESPACE}/closeAddDueSuccessDialog`
)

export const setInvoicesSearching = createAction<string>(
  `${NAMESPACE}/setInvoicesSearch`
)

export const setAddInvoiceDialogState = createAction<AddInvoiceDialogState>(
  `${NAMESPACE}/setAddInvoiceDialogState`
)

export const setImportDialogState = createAction<boolean>(
  `${NAMESPACE}/setImportDialogState`
)
export const setFilePreviewDialogIsOpen = createAction<boolean>(
  `${NAMESPACE}/setFilePreviewDialogIsOpen`
)

export const setFilePreviewError = createAction<FileImportErrorCode | null>(
  `${NAMESPACE}/setFilePreviewError`
)

export const downloadImportReport = createAction<string>(
  `${NAMESPACE}/downloadImportReport`
)

export const setCollapsedInvoiceId = createAction<number | null>(
  `${NAMESPACE}/setCollapsedInvoiceId`
)

export const patchMapping = createAction(
  `${NAMESPACE}/patchMapping`,
  function prepare(fileColumn: string, requiredColumn: string) {
    return {
      payload: {
        requiredColumn,
        fileColumn,
      },
    }
  }
)
export const setMapping = createAction<Record<string, string>>(
  `${NAMESPACE}/setMapping`
)

export const deleteMapping = createAction<string>(`${NAMESPACE}/deleteMapping`)

export const setFileImportExistingInvoicesDialogState = createAction<{
  isDialogOpen: boolean
}>(`${NAMESPACE}/setFileImportExistingInvoicesDialogState`)

export const setFileImportLawsuitAlreadyFiledDialogState = createAction<{
  isDialogOpen: boolean
}>(`${NAMESPACE}/setFileImportLawsuitAlreadyFiledDialogState`)

export const setFileImportSummaryDialogState = createAction<{
  isDialogOpen: boolean
}>(`${NAMESPACE}/setFileImportSummaryDialogState`)

export const setFileImportSummaryReportDialogState = createAction<{
  isDialogOpen: boolean
}>(`${NAMESPACE}/setFileImportSummaryReportDialogState`)

export const toggleRefetchInvoices = createAction(
  `${NAMESPACE}/toggleRefetchInvoices`
)

export const setPreTrialRequestInvoiceData = createAction<
  Pick<Invoice, 'contractorName' | 'id' | 'number'>
>(`${NAMESPACE}/setPreTrialRequestInvoiceData`)

export const setGeneratePreTrialRequestForPaymentDialogOpen =
  createAction<boolean>(
    `${NAMESPACE}/setGeneratePreTrialRequestForPaymentDialogOpen`
  )

export const setGeneratePreTrialRequestForPaymentSuccessDialogOpen =
  createAction<boolean>(
    `${NAMESPACE}/setGeneratePreTrialRequestForPaymentSuccessDialogOpen`
  )

export const clearMapping = createAction(`${NAMESPACE}/clearMapping`)

export const resetBatchActionsState = createAction(
  `${NAMESPACE}/resetBatchActionsState`
)

export const getImportTemplate = createAsyncThunk<
  void,
  { fileName: string },
  { rejectValue: string }
>(
  `${NAMESPACE}/getImportTemplate`,
  async ({ fileName }, { rejectWithValue }) => {
    try {
      const response = await Api.get<string>(paths.Files.invoiceImport, {
        responseType: 'blob',
      })
      const blob = response.data
      downloadFile(blob, fileName)
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const addInvoice = createAsyncThunk<
  AddInvoiceResponse,
  {
    request: AddInvoiceRequest
  },
  { rejectValue: string }
>(`${NAMESPACE}/addInvoice`, async ({ request }, thunkAPI) => {
  try {
    const response = await Api.post<AddInvoiceResponse>(paths.Invoices.root, {
      ...request,
      amountLeft: request.amount,
    })
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getInvoice = createAsyncThunk<
  { response: GetInvoiceResponse },
  string,
  { rejectValue: string }
>(`${NAMESPACE}/getInvoice`, async (invoiceNumber, thunkAPI) => {
  try {
    const response = await Api.get(`${paths.Invoices.root}/number`, {
      params: {
        number: invoiceNumber,
      },
    })
    return { response }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const deleteInvoice = createAsyncThunk<
  void,
  number,
  { rejectValue: string }
>(`${NAMESPACE}/deleteInvoice`, async (id, thunkAPI) => {
  try {
    await Api.delete(`${paths.Invoices.root}/${id}`)
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const setInvoiceAsPaid = createAsyncThunk<
  void,
  number,
  { rejectValue: string }
>(`${NAMESPACE}/setInvoiceAsPaid`, async (id, thunkAPI) => {
  try {
    await Api.put(`${paths.Invoices.root}/${id}/set-paid`)
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getFilePreview = createAsyncThunk<
  { response: GetFilePreviewResponse },
  { file: File },
  { rejectValue: { error: string; errorCode: string } }
>(`${NAMESPACE}/getFilePreview`, async ({ file }, thunkAPI) => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    const response = await Api.post<
      AxiosRequestConfig,
      AxiosResponse<GetFilePreviewResponse>
    >(`${paths.Invoices.importPreview}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return { response: { ...response.data, fileName: file.name } }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getInvoicesImportColumns = createAsyncThunk<
  { response: string[] },
  undefined,
  { rejectValue: string }
>(`${NAMESPACE}/getInvoicesImportColumns`, async (_, thunkAPI) => {
  try {
    const response = await Api.get(paths.Invoices.columns)
    return { response: response.data }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getFileImportSummary = createAsyncThunk<
  { response: GetFileImportSummaryResponse },
  FileWithMappingOperationPayload,
  { rejectValue: { error: string; errorCode: string } }
>(
  `${NAMESPACE}/getFileImportSummary`,
  async ({ file, mappings, saveMapping }, { rejectWithValue, getState }) => {
    const selectedCompanyId = selectors.companies.getSelectedCompanyId(
      getState() as RootState
    )
    const formData = new FormData()
    formData.append('file', file)
    formData.append('mappings', JSON.stringify(invert(mappings)))
    formData.append('saveMappings', JSON.stringify(saveMapping))
    if (selectedCompanyId) {
      formData.append('companyId', selectedCompanyId)
    }

    try {
      const response = await Api.post<
        AxiosRequestConfig,
        AxiosResponse<GetFileImportSummaryResponse>
      >(`${paths.Invoices.importSummary}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      const {
        deletedInvoices,
        newInvoices,
        updatedInvoices,
        newContractors,
        invalidInvoices,
      } = response.data.importSummary
      const areOnlyInvalid =
        !deletedInvoices?.count &&
        !newInvoices?.count &&
        !newContractors &&
        !updatedInvoices &&
        invalidInvoices
      if (areOnlyInvalid) throw new Error('')
      return { response: response.data }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const acceptImport = createAsyncThunk<
  Blob,
  FileWithMappingOperationPayload,
  { rejectValue: { error: string; errorCode: string } }
>(
  `${NAMESPACE}/saveMappings`,
  async ({ file, mappings, saveMapping }, { rejectWithValue, getState }) => {
    const selectedCompanyId = selectors.companies.getSelectedCompanyId(
      getState() as RootState
    )
    const formData = new FormData()
    formData.append('file', file)
    formData.append('mappings', JSON.stringify(invert(mappings)))
    formData.append('saveMappings', JSON.stringify(saveMapping))
    if (selectedCompanyId) {
      formData.append('companyId', selectedCompanyId)
    }

    try {
      const response = await Api.post<AxiosRequestConfig, AxiosResponse<Blob>>(
        `${paths.Invoices.saveMappings}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          responseType: 'blob',
        }
      )

      return response.data as Blob
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const getMapping = createAsyncThunk<
  Record<string, string>,
  undefined,
  { rejectValue: { error: string; errorCode: string } }
>(`${NAMESPACE}/getMappings`, async (_, thunkAPI) => {
  try {
    const response = await Api.get<
      AxiosRequestConfig,
      AxiosResponse<Record<string, string>>
    >(paths.Invoices.getMappings)
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const validateInvoice = createAsyncThunk<
  { response: GetInvoiceResponse },
  string,
  { rejectValue: { error: string; errorCode: string } }
>(`${NAMESPACE}/validateInvoice`, async (invoiceNumber, thunkAPI) => {
  try {
    const response = await Api.get(
      `${paths.Invoices.root}/number/${encodeURIComponent(
        invoiceNumber
      )}/validate`
    )
    return { response }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getLastInvoiceImports = createAsyncThunk<
  InvoiceImport[],
  string,
  { rejectValue: { error: string; errorCode: string } }
>(`${NAMESPACE}/getLastInvoiceImports`, async (companyId, thunkAPI) => {
  try {
    const response = await Api.get(paths.Invoices.imports, {
      params: {
        companyId,
      },
    })
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getPreTrialPaymentRequestPreview = createAsyncThunk<
  void,
  string | number,
  { rejectValue: { error: string; errorCode: string } }
>(
  `${NAMESPACE}/getPreTrialPaymentRequestPreview`,
  async (invoiceId, thunkAPI) => {
    try {
      const response = await Api.get(
        `${paths.Invoices.root}/${invoiceId}/pre-trial-payment-request/preview`,
        {
          responseType: 'blob',
        }
      )
      const blob = response.data
      const href = window.URL.createObjectURL(blob)
      window.open(href, '_blank')
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const sendMultipleNotifications = createAsyncThunk<
  MultipleNotificationStatus,
  number[],
  { rejectValue: { error: string; errorCode: string }[] }
>(`${NAMESPACE}/sendMultipleNotifications`, async (ids, thunkAPI) => {
  try {
    const response = await Api.post(paths.Invoices.sendNotification, {
      ids,
    })
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const setMultipleInvoicesAsPaid = createAsyncThunk<
  MultipleSetAsPaidStatus,
  number[],
  { rejectValue: { error: string; errorCode: string } }
>(`${NAMESPACE}/setMultipleInvoicesAsPaid`, async (ids, thunkAPI) => {
  try {
    const response = await Api.put(paths.Invoices.setPaid, {
      ids,
    })
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const deleteMultipleInvoices = createAsyncThunk<
  MultipleDeleteStatus,
  number[],
  { rejectValue: { error: string; errorCode: string } }
>(`${NAMESPACE}/deleteMultipleInvoices`, async (ids, thunkAPI) => {
  try {
    const response = await Api.delete(paths.Invoices.delete, {
      data: {
        ids,
      },
    })
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getInvoiceHistory = createAsyncThunk<
  InvoiceHistoryItem[],
  number | string,
  { rejectValue: string }
>(`${NAMESPACE}/getInvoiceHistory`, async (id, thunkAPI) => {
  try {
    const response = await Api.get(`${paths.Invoices.root}/${id}/history`)
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})
